'use client';
import { useAppStore } from '@/stores/app-store';
import { canAccessSysPages } from '@/lib/sys-access';
import { t } from '@/lib/i18n';
import { hasPerm } from '@/lib/permissions';
import { isAddonEnabled } from '@/lib/entitlements';
import { DEV_TYPES, hasGensetFields } from '@/lib/device-types';
import { isTempDevice } from '@/lib/coldchain';
import Avatar from './Avatar';
import Wordmark from './Wordmark';
import { useSite } from './SiteProvider';
import { isPoultrySite } from '@/lib/custom-logins';
import { tenantTab } from '@/lib/tenant-tab';
import { hiddenPageIds } from '@/lib/site-menu';
import type { PageId } from '@/lib/types';
import { PM_PAGES, pmMenuLabel } from './PmTabs';

export default function Sidebar() {
  const { currentUser, dbUser, currentPlant, page, lang, plants, sidebarCollapsed, mobileNavOpen, setPage, setCurrentPlant, toggleSidebar, toggleTheme, toggleLang, logout } = useAppStore();
  const site = useSite();
  // Pages this subdomain has hidden (Site Manager → menu visibility).
  const hidden = hiddenPageIds(site.hiddenPages);

  if (!currentUser) return null;

  const getPlantCategories = (pid: string): string[] => {
    const cats: string[] = [];
    (plants[pid]?.devices || []).forEach((d) => {
      const dt = DEV_TYPES[d.type];
      if (dt && !cats.includes(dt.cat)) cats.push(dt.cat);
    });
    return cats;
  };

  const navTo = (p: string, clearPlant = false) => {
    // Viewers keep their selected plant even on global-nav pages: clearing it
    // would trip the "auto-select first plant → dashboard" self-heal in
    // app/page.tsx and bounce them straight back to the dashboard.
    if (clearPlant && currentUser.role !== 'viewer') setCurrentPlant(null);
    setPage(p as any);
  };

  const th = lang === 'th';
  // สิทธิ์ AI ระดับบัญชี — server ตัดสินมาใน dbUser.features (lib/session-flags.ts)
  // ใช้ร่วมกันทั้งเมนู AI ของทุกไซต์และของไซต์เดียว จะได้ไม่เพี้ยนกัน
  const aiOn = isAddonEnabled('ai') && (dbUser ? dbUser.features?.ai !== false : true) && hasPerm(currentUser, 'ai');
  const calOn = dbUser ? dbUser.features?.calibration !== false : true;

  const cats = currentPlant ? getPlantCategories(currentPlant) : [];
  // Dormitory add-on: when the selected plant runs dorm mode, the per-plant menu
  // shows the dorm workspace instead of the monitoring items.
  const isDormMode = isAddonEnabled('dorm') && !!(currentPlant && plants[currentPlant]?.dorm?.enabled);
  const dormItems: { id: PageId; label: string; icon: string }[] = [
    { id: 'dormoverview', label: lang === 'th' ? 'ภาพรวมหอพัก' : 'Overview', icon: 'M3 3v18h18 M7 13v5 M12 8v10 M17 11v7' },
    { id: 'dormrooms', label: lang === 'th' ? 'ห้อง' : 'Rooms', icon: 'M3 21V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v16 M3 21h18 M9 8h6 M9 12h6 M9 16h6' },
    { id: 'dormtenants', label: lang === 'th' ? 'ผู้เช่า' : 'Tenants', icon: 'M16 21v-2a4 4 0 0 0-8 0v2 M12 7a4 4 0 1 0 0 0.01' },
    { id: 'dormmeter', label: lang === 'th' ? 'จดมิเตอร์' : 'Meter reading', icon: 'M4 20h16 M6 16l3-4 3 2 4-6 M6 6h.01' },
    { id: 'dormbill', label: lang === 'th' ? 'บิล & ชำระเงิน' : 'Billing', icon: 'M6 2h9l5 5v15H6z M14 2v6h6 M9 13h6 M9 17h6' },
  ];

  // SYSTEM section — the label renders when at least one of its entries does.
  // Packages & Pricing — the price list is still being settled, so it stays on
  // the internal accounts for now. To open it to every tenant admin, swap the
  // guard for `hasPerm(currentUser, 'usermgmt')`.
  const showPackages = canAccessSysPages(currentUser.email) && !hidden.has('packages');
  // บัญชีของฉันอยู่ที่นี่ที่เดียว — เมนู avatar บน Topbar เหลือแค่ออกจากระบบ
  const showProfile = !hidden.has('profile');
  // User Management — server ตัดสินมาแล้วตอน login/restore (dbUser.canManageUsers)
  // เพราะเงื่อนไขบนไซต์หลักคือ "เป็นเจ้าของอุปกรณ์หรือ Plant ไหม" ซึ่งหน้าเว็บ
  // ไม่มีทางรู้เอง — ดู lib/can-manage-users.ts · โหมด demo ไม่มี dbUser จึงถอย
  // ไปใช้ role เหมือนเดิม
  const showUserMgmt = (dbUser ? dbUser.canManageUsers === true : currentUser.role === 'admin')
    && !hidden.has('usermgmt');
  // The manual entry only appears where a manual exists for this site
  // (`site.manualUrl`) — customer subdomains without their own edition get none.
  const showManual = !hidden.has('manual') && !!site.manualUrl;

  const collapseTitle = lang === 'th'
    ? (sidebarCollapsed ? 'ขยายเมนู' : 'ย่อเมนู')
    : sidebarCollapsed ? 'Expand menu' : 'Collapse menu';

  return (
    <>
    {/* Rendered OUTSIDE .sidebar on purpose. The sidebar sets overflow-y:auto,
        which makes overflow-x compute to auto as well, so anything straddling
        its edge gets clipped — the pip only ever showed its inner half. As a
        fixed sibling it sits on the seam uncut, and follows the rail's width. */}
    <style>{`
      .collapse-dot {
        position: fixed; top: 43px; left: 250px; z-index: 101;
        width: 26px; height: 26px; border-radius: 50%;
        /* Centred by transform, so the ring sits exactly on the seam. */
        transform: translate(-50%, -50%);
        border: 1px solid var(--gold); background: var(--bg-elevated);
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        color: var(--gold); padding: 0;
        box-shadow: 0 2px 10px var(--gold-glow);
        transition: box-shadow .2s, background .2s,
                    left .35s cubic-bezier(.16,1,.3,1);
      }
      .collapse-dot.collapsed { left: 68px; }
      /* A little slack around the 26px ring so it's easy to hit. */
      .collapse-dot::before { content: ''; position: absolute; inset: -6px; border-radius: 50%; }
      .collapse-dot svg { transition: transform .35s; }
      /* Always the full ring; hover only deepens the glow. */
      .collapse-dot:hover, .collapse-dot:focus-visible {
        background: var(--gold-dim); box-shadow: 0 2px 18px var(--gold-glow);
      }
      .collapse-dot.collapsed svg { transform: rotate(180deg); }
      @media (max-width: 900px) { .collapse-dot { display: none; } }

      /* Menu row spacing. Lives here rather than in globals.css because that
         stylesheet's chunk filename is fixed in dev and Cloudflare serves it
         with max-age=14400 — edits to it stay invisible for up to 4 hours. */
      .sidebar .nav-btn { padding: 3px 14px; margin-bottom: 0; }
      .sidebar.collapsed .nav-btn { padding: 6px; }
      .sidebar .nav-label { margin: 12px 0 4px; }
      @media (max-width: 900px) { .sidebar.collapsed .nav-btn { padding: 3px 14px; } }
    `}</style>
    <button
      className={`collapse-dot${sidebarCollapsed ? ' collapsed' : ''}`}
      onClick={toggleSidebar}
      aria-label={collapseTitle}
      title={collapseTitle}
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
        <polyline points="10 3 5 8 10 13" />
      </svg>
    </button>
    <div className={`sidebar${sidebarCollapsed ? ' collapsed' : ''}${mobileNavOpen ? ' mobile-open' : ''}`}>
      {/* Brand
          ไซต์ของเรา → ชื่อบริษัทเป็นตัวอักษรแบบเดียวกับเว็บหลัก (ก่อนหน้านี้ใช้
          รูปดาวกลมซึ่งเป็นคนละแบรนด์กับที่เว็บใช้) · ไซต์ลูกค้า White Label →
          โลโก้ของเขาเหมือนเดิม ห้ามเอาแบรนด์เราไปแปะทับ

          ใช้ site.isDefault ไม่ใช่ site.hasLogo เพราะไซต์หลักก็มีไฟล์โลโก้
          อัปโหลดไว้ใน DB เหมือนกัน (รูปดาวกลม 36KB) เงื่อนไข hasLogo จึงไม่แยก
          "ไซต์ของเรา" ออกจาก "ไซต์ลูกค้า" ส่วน is_default มีแถวเดียวทั้งตาราง
          และคือไซต์หลักพอดี */}
      <div className="sidebar-brand">
        {!site.isDefault ? (
          <>
            <div className="sidebar-logo">
              {/* collapsed rail: a wide wordmark is unreadable at 44px, so tenants with a
                  bundled square icon (lib/tenant-tab.ts) show that instead */}
              {/* a tenant with per-theme transparent wordmarks (lib/tenant-tab.ts) shows the one
                  for the current theme; the <style> lives here because globals.css is CDN-cached */}
              {!sidebarCollapsed && tenantTab(site.cusGroup)?.logo ? (<>
                <style>{`[data-theme="dark"] .sb-logo-light{display:none!important}[data-theme="light"] .sb-logo-dark{display:none!important}`}</style>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className="sb-logo-light" src={tenantTab(site.cusGroup)!.logo!.light} alt={site.sidebarText || 'logo'} style={{ maxWidth: 170, height: 46 }} />
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className="sb-logo-dark" src={tenantTab(site.cusGroup)!.logo!.dark} alt={site.sidebarText || 'logo'} style={{ maxWidth: 170, height: 46 }} />
              </>) : (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={(sidebarCollapsed && tenantTab(site.cusGroup)?.icon) || site.logoUrl || '/api/site/logo'}
                alt={site.sidebarText || 'logo'}
                /* sizing lives in .sidebar-logo img (globals.css) so the collapsed
                   and mobile breakpoints can override it */
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo.png'; }}
              />)}
            </div>
            <div className="hide-collapsed">
              {site.sidebarText && <h3>{site.sidebarText}</h3>}
            </div>
          </>
        ) : (
          <>
            {/* โลโก้วงกลมเดิมอยู่คู่กับชื่อ — เจ้าของแบรนด์เลือกแบบนี้
                (ต่างจากกติกาของเว็บหลักที่ไม่มีสัญลักษณ์คู่ชื่อ)
                ตอนย่อเมนูเหลือรูปอย่างเดียว ชื่อถูกซ่อนด้วย .hide-collapsed */}
            <div className="sidebar-logo">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={site.hasLogo ? site.logoUrl || '/api/site/logo' : '/logo.png'}
                alt={site.siteName}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo.png'; }}
              />
            </div>
            <div className="hide-collapsed" style={{ color: 'var(--text-primary)' }}>
              <Wordmark size={17} />
            </div>
          </>
        )}
      </div>

      {/* ── ทั้งระบบ ──────────────────────────────────────────────────
          หมวดนี้กับหมวดของไซต์ด้านล่างอยู่คนละขอบเขตกัน แต่เดิมแยกไม่ออก
          เพราะหมวดล่างใช้ "ชื่อไซต์" เป็นหัวข้อ ไม่มีคำบอกว่าเป็นของไซต์เดียว */}
      <div className="nav-label hide-collapsed">{th ? 'ทั้งระบบ' : 'FLEET'}</div>

      {/* Operation Center + All Plants — visible to ALL roles (read-only for
          viewers; plant list is already scoped to bound plants server-side). */}
      <button className={`nav-btn${page === 'overview' ? ' active' : ''}`} onClick={() => navTo('overview', true)}>
        <span className="ni">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
            <rect x="3" y="3" width="7" height="7" rx="2" />
            <rect x="14" y="3" width="7" height="7" rx="2" />
            <rect x="3" y="14" width="7" height="7" rx="2" />
            <rect x="14" y="14" width="7" height="7" rx="2" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
            <line x1="10" y1="12" x2="7" y2="12" /><line x1="14" y1="12" x2="17" y2="12" />
            <line x1="12" y1="10" x2="12" y2="7" /><line x1="12" y1="14" x2="12" y2="17" />
          </svg>
        </span>
        <span className="nav-text">{t('overview', lang)}</span>
      </button>

      {/* poultry sites (Saha Farms): every farm grouped by สาขา, worst house first (user 2026-10-29: company → branch → farm) */}
      {isPoultrySite(site.cusGroup) && (
        <button className={`nav-btn${page === 'farmoverview' ? ' active' : ''}`} onClick={() => navTo('farmoverview', true)}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" /><circle cx="12" cy="11" r="2.2" />
            </svg>
          </span>
          <span className="nav-text">{th ? 'ฟาร์มตามสาขา' : 'Farms by branch'}</span>
        </button>
      )}

      <button className={`nav-btn${page === 'plants' ? ' active' : ''}`} onClick={() => navTo('plants', true)}>
        <span className="ni">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
            <rect x="2" y="8" width="8" height="14" rx="1.5" />
            <rect x="14" y="3" width="8" height="19" rx="1.5" />
            <line x1="4.5" y1="11" x2="7.5" y2="11" />
            <line x1="4.5" y1="14" x2="7.5" y2="14" />
            <line x1="4.5" y1="17" x2="7.5" y2="17" />
            <line x1="16.5" y1="6" x2="19.5" y2="6" />
            <line x1="16.5" y1="9" x2="19.5" y2="9" />
            <line x1="16.5" y1="12" x2="19.5" y2="12" />
            <line x1="16.5" y1="15" x2="19.5" y2="15" />
            <circle cx="18" cy="1.5" r="0.5" fill="currentColor" stroke="none" />
            <line x1="18" y1="2" x2="18" y2="3" />
          </svg>
        </span>
        <span className="nav-text">{t('plants', lang)}</span>
      </button>

      {/* Device Overview — all devices across every plant with live values (like 3.0) */}
      <button className={`nav-btn${page === 'deviceoverview' ? ' active' : ''}`} onClick={() => navTo('deviceoverview', true)}>
        <span className="ni">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
        </span>
        <span className="nav-text">{lang === 'th' ? 'ภาพรวมอุปกรณ์' : 'Device Overview'}</span>
      </button>

      {/* Device Monitor — device telemetry viewer (global nav; keeps current plant) */}
      {!hidden.has('monitor2') && (
      <button className={`nav-btn${page === 'monitor2' ? ' active' : ''}`} onClick={() => navTo('monitor2')}>
        <span className="ni">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
            <rect x="2" y="4" width="20" height="13" rx="2" />
            <path d="M6 11l3-3 2 2 3-4 4 5" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </span>
        <span className="nav-text">{t('monitor2', lang)}</span>
      </button>
      )}

      {/* Connectivity — Resonac-only top-level board for the recurring "devices
          keep dropping" problem (uptime + cluster correlation + last-seen).
          navTo keeps the current plant; the page falls back to the first plant. */}
      {site.cusGroup === 'resonac' && (
        <button className={`nav-btn${page === 'connectivity' ? ' active' : ''}`} onClick={() => navTo('connectivity')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          </span>
          <span className="nav-text">{lang === 'th' ? 'สถานะการเชื่อมต่อ' : 'Connectivity'}</span>
        </button>
      )}

      {/* Device Setting (formerly My Devices) — moved below Device Monitor */}
      {hasPerm(currentUser, 'devices') && (
        <button className={`nav-btn${page === 'mydevices' ? ' active' : ''}`} onClick={() => navTo('mydevices')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <rect x="7" y="7" width="10" height="10" rx="1" />
              <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
              <line x1="7" y1="10" x2="4" y2="10" /><line x1="7" y1="14" x2="4" y2="14" />
              <line x1="17" y1="10" x2="20" y2="10" /><line x1="17" y1="14" x2="20" y2="14" />
              <line x1="10" y1="7" x2="10" y2="4" /><line x1="14" y1="7" x2="14" y2="4" />
              <line x1="10" y1="17" x2="10" y2="20" /><line x1="14" y1="17" x2="14" y2="20" />
            </svg>
          </span>
          <span className="nav-text">{t('mydevices', lang)}</span>
        </button>
      )}

      {/* ── ปัญญา & รายงาน ───────────────────────────────────────────
          ชั้นที่หายไป — AI กับรายงานเคยอยู่ใต้ไซต์ ทำให้ลูกค้าที่มีสิบไซต์
          ต้องคลิกเข้าไปดูทีละอันถึงจะรู้ว่าที่ไหนผิดปกติ ซึ่งกลับหัวกับ
          ธรรมชาติของฟีเจอร์ · หน้าของไซต์ยังอยู่เหมือนเดิมในฐานะปลายทาง
          ที่เจาะลงไป */}
      {currentUser && (
        <div className="nav-label hide-collapsed">{th ? 'ปัญญา & รายงาน' : 'INSIGHTS & REPORTS'}</div>
      )}
      {currentUser && aiOn && (
        <button className={`nav-btn${page === 'aifleet' ? ' active' : ''}`} onClick={() => navTo('aifleet', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
              <circle cx="12" cy="12" r="4" />
            </svg>
          </span>
          <span className="nav-text">{t('fleetInsights', lang)}</span>
        </button>
      )}
      {currentUser && (
        <button className={`nav-btn${page === 'reports' ? ' active' : ''}`} onClick={() => navTo('reports', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <polyline points="14 3 14 8 19 8" />
              <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" />
            </svg>
          </span>
          <span className="nav-text">{t('reportCenter', lang)}</span>
        </button>
      )}
      {currentUser && (
        <button className={`nav-btn${page === 'cockpit' ? ' active' : ''}`} onClick={() => navTo('cockpit', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <rect x="2" y="4" width="20" height="13" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              <path d="M6 13l3-3 2.5 2.5L15 8l3 3" />
            </svg>
          </span>
          <span className="nav-text">{t('cockpit', lang)}</span>
        </button>
      )}

      {/* ── ไซต์ที่เปิดอยู่ ─────────────────────────────────────────── */}
      {currentPlant && plants[currentPlant] && (
        <>
          <div className="nav-label hide-collapsed" style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ opacity: 0.6 }}>{th ? 'ไซต์' : 'SITE'}</span>
            <span style={{ color: 'var(--accent)' }}>{plants[currentPlant].name.substring(0, 20)}</span>
          </div>

          {isDormMode && (
            <>
              {dormItems.map((it) => (
                <button
                  key={it.id}
                  className={`nav-btn${page === it.id ? ' active' : ''}`}
                  onClick={() => setPage(it.id)}
                >
                  <span className="ni">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                      <path d={it.icon} />
                    </svg>
                  </span>
                  <span className="nav-text">{it.label}</span>
                </button>
              ))}
            </>
          )}

          {!isDormMode && (
          <>
          {hasPerm(currentUser, 'dashboard') && (
            <button className={`nav-btn${page === 'dashboard' ? ' active' : ''}`} onClick={() => setPage('dashboard')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <rect x="3" y="3" width="7" height="10" rx="1.5" />
                  <rect x="14" y="3" width="7" height="6" rx="1.5" />
                  <rect x="14" y="12" width="7" height="9" rx="1.5" />
                  <rect x="3" y="16" width="7" height="5" rx="1.5" />
                  <line x1="5" y1="6" x2="8" y2="6" /><line x1="5" y1="8" x2="7" y2="8" />
                  <circle cx="17" cy="16" r="1" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <span className="nav-text">{t('dashboard', lang)}</span>
            </button>
          )}

          {/* Device Overview — all devices grouped by category (like NG) */}
          <button className={`nav-btn${page === 'plantoverview' ? ' active' : ''}`} onClick={() => setPage('plantoverview')}>
            <span className="ni">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" />
              </svg>
            </span>
            <span className="nav-text">{t('plantOverview', lang)}</span>
          </button>

          {/* Monitor — device telemetry viewer with chart + event log (like NG) */}
          {!hidden.has('monitor') && (
          <button className={`nav-btn${page === 'monitor' ? ' active' : ''}`} onClick={() => setPage('monitor')}>
            <span className="ni">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                <rect x="2" y="4" width="20" height="13" rx="2" />
                <path d="M6 11l3-3 2 2 3-4 4 5" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </span>
            <span className="nav-text">{t('monitor', lang)}</span>
          </button>
          )}

          {/* MDB Health / Power Quality */}
          {!hidden.has('mdbhealth') && (
            <button className={`nav-btn${page === 'mdbhealth' ? ' active' : ''}`} onClick={() => setPage('mdbhealth')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M12 21s-7-4.5-9-9a5 5 0 0 1 9-2 5 5 0 0 1 9 2c-.6 1.5-1.7 2.9-3 4.1" />
                  <path d="M8 12h2l1.5-3 2 5 1.5-2h3" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'สุขภาพ MDB / Power Quality' : 'MDB Health / Power Quality'}</span>
            </button>
          )}

          {/* Energy & Carbon (ESG) */}
          {!hidden.has('carbon') && (
            <button className={`nav-btn${page === 'carbon' ? ' active' : ''}`} onClick={() => setPage('carbon')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
                  <path d="M2 21c0-3 1.85-5.36 5.08-6" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'พลังงาน & คาร์บอน' : 'Energy & Carbon'}</span>
            </button>
          )}

          {isAddonEnabled('utility') && cats.includes('utility') && !hidden.has('water') && (
            <button className={`nav-btn${page === 'water' ? ' active' : ''}`} onClick={() => setPage('water' as any)}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M12 2.7c0 0-8 7.3-8 13.3a8 8 0 0 0 16 0c0-6-8-13.3-8-13.3z" />
                  <path d="M6 16h12" /><path d="M7 19h10" />
                  <circle cx="18" cy="6" r="3" /><path d="M17 5v2M16 6h4" />
                </svg>
              </span>
              <span className="nav-text">Utility</span>
            </button>
          )}

          {cats.includes('solar') && hasPerm(currentUser, 'solar') && !hidden.has('solar') && (
            <button className={`nav-btn${page === 'solar' ? ' active' : ''}`} onClick={() => setPage('solar')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M6 18L4 22h16l-2-4" />
                  <rect x="4" y="8" width="16" height="10" rx="1" />
                  <line x1="8" y1="11" x2="8" y2="15" /><line x1="12" y1="11" x2="12" y2="15" /><line x1="16" y1="11" x2="16" y2="15" />
                  <line x1="4" y1="13" x2="20" y2="13" />
                  <circle cx="19" cy="5" r="2.5" />
                  <line x1="19" y1="3" x2="19" y2="2" /><line x1="19" y1="8" x2="19" y2="7.5" />
                  <line x1="21" y1="5" x2="21.5" y2="5" /><line x1="16.5" y1="5" x2="17" y2="5" />
                </svg>
              </span>
              <span className="nav-text">{t('solar', lang)}</span>
            </button>
          )}

          {/* AI ของไซต์นี้ — ใช้ aiOn ตัวเดียวกับเมนู AI ทุกไซต์ด้านบน
              จะได้ไม่มีวันเปิดอันหนึ่งแต่ปิดอีกอัน (ดู aiOn ที่หัวไฟล์) */}
          {aiOn && !hidden.has('ai') && (
            <button className={`nav-btn${page === 'ai' ? ' active' : ''}`} onClick={() => setPage('ai')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <ellipse cx="12" cy="12" rx="8" ry="7" />
                  <circle cx="8" cy="11" r="1" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="9" r="1" fill="currentColor" stroke="none" />
                  <circle cx="16" cy="11" r="1" fill="currentColor" stroke="none" />
                  <circle cx="10" cy="15" r="1" fill="currentColor" stroke="none" />
                  <circle cx="14" cy="15" r="1" fill="currentColor" stroke="none" />
                  <line x1="8" y1="11" x2="12" y2="9" /><line x1="12" y1="9" x2="16" y2="11" />
                  <line x1="8" y1="11" x2="10" y2="15" /><line x1="16" y1="11" x2="14" y2="15" />
                  <line x1="10" y1="15" x2="14" y2="15" />
                  <path d="M7 5L5 3" /><path d="M17 5l2-2" />
                </svg>
              </span>
              <span className="nav-text">{t('aiInsights', lang)}</span>
            </button>
          )}

          {/* Plant Report — generator sites only: parameter tables, real trends
              and the recorded alarm log. Gated on configured fields rather than
              plant_type, since a genset site is usually filed as 'factory'. */}
          {hasGensetFields(plants[currentPlant]?.devices) && !hidden.has('plantreport') && (
            <button className={`nav-btn${page === 'plantreport' ? ' active' : ''}`} onClick={() => setPage('plantreport')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'รายงานไซต์' : 'Site Report'}</span>
            </button>
          )}

          {/* Aeration & DO — wastewater sites only: correlate aeration pumps vs DO */}
          {plants[currentPlant]?.type === 'water' && !hidden.has('aeration') && (
            <button className={`nav-btn${page === 'aeration' ? ' active' : ''}`} onClick={() => setPage('aeration' as any)}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M3 16c1.5 0 1.5-2 3-2s1.5 2 3 2 1.5-2 3-2 1.5 2 3 2 1.5-2 3-2 1.5 2 3 2" />
                  <circle cx="7" cy="7" r="1.4" /><circle cx="12" cy="5" r="1.4" /><circle cx="16" cy="8" r="1.4" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'การเติมอากาศ & DO' : 'Aeration & DO'}</span>
            </button>
          )}

          {/* Electricity Summary — history table (kWh + ฿) with Excel export */}
          {plants[currentPlant]?.type === 'water' && !hidden.has('energybill') && (
            <button className={`nav-btn${page === 'energybill' ? ' active' : ''}`} onClick={() => setPage('energybill' as any)}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M4 3h16v18l-3-1.5L14 21l-2-1.5L10 21l-3-1.5L4 21z" />
                  <line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'สรุปค่าไฟย้อนหลัง' : 'Electricity Summary'}</span>
            </button>
          )}

          {/* Monthly temperature record (แบบบันทึกอุณหภูมิตู้เย็นเก็บวัคซีน) —
              sites with a temperature logger only. A plant of power meters has
              nothing to put on the form. */}
          {(plants[currentPlant]?.devices || []).some(isTempDevice) && !hidden.has('tempreport') && (
            <button className={`nav-btn${page === 'tempreport' ? ' active' : ''}`} onClick={() => setPage('tempreport')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M9 18v-4" /><path d="M12 18v-6" /><path d="M15 18v-3" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'รายงานอุณหภูมิรายเดือน' : 'Monthly Temp Report'}</span>
            </button>
          )}

          {/* Location — visible to all roles in the plant (read-only map for
              viewers; edit controls inside the page self-gate on psettings). */}
          <button className={`nav-btn${page === 'location' ? ' active' : ''}`} onClick={() => setPage('location')}>
            <span className="ni">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </span>
            <span className="nav-text">{t('location', lang)}</span>
          </button>

          </>
          )}

          {hasPerm(currentUser, 'psettings') && (
            <button className={`nav-btn${page === 'psettings' ? ' active' : ''}`} onClick={() => setPage('psettings')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 2v2" /><path d="M12 20v2" />
                  <path d="M4.93 4.93l1.41 1.41" /><path d="M17.66 17.66l1.41 1.41" />
                  <path d="M2 12h2" /><path d="M20 12h2" />
                  <path d="M4.93 19.07l1.41-1.41" /><path d="M17.66 6.34l1.41-1.41" />
                  <line x1="12" y1="9" x2="12" y2="8" /><line x1="14.1" y1="10" x2="15" y2="9.5" />
                  <line x1="14.1" y1="14" x2="15" y2="14.5" /><line x1="9.9" y1="10" x2="9" y2="9.5" />
                </svg>
              </span>
              <span className="nav-text">{t('psettings', lang)}</span>
            </button>
          )}
        </>
      )}

      {/* Preventive Maintenance — visible to all roles. Viewers get a
          read-only view (no record). Don't clear the selected plant for
          viewers, or their plant-specific sidebar nav would disappear. */}
      {/* หมวดงานเดินระบบ — ศูนย์แจ้งเตือนอยู่ข้าง PM เพราะที่นี่คือที่ที่งานเกิด
          (แจ้งเตือน → เปิดใบงาน) แต่ไม่ถูกกั้นด้วย features.pm เพราะการแจ้งเตือน
          เป็นของหลักของระบบ ไม่ใช่ add-on บำรุงรักษา */}
      {currentUser && (
        <div className="nav-label hide-collapsed" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* เขียน & ตรง ๆ — นี่คือสตริงใน JS ไม่ใช่ข้อความใน JSX ใส่ &amp;
              แล้วจะขึ้นจอว่า "O&AMP;M" ตามตัวอักษร */}
          {th ? 'งานบำรุงรักษา' : 'O&M'}
        </div>
      )}
      {currentUser && (
        <button className={`nav-btn${page === 'alarms' ? ' active' : ''}`} onClick={() => navTo('alarms', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
          </span>
          <span className="nav-text">{t('alarmCenter', lang)}</span>
        </button>
      )}
      {currentUser && calOn && !hidden.has('calibration') && (
        <button className={`nav-btn${page === 'calibration' ? ' active' : ''}`} onClick={() => navTo('calibration', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M3 17h18" /><path d="M6 17V9" /><path d="M12 17V5" /><path d="M18 17v-6" />
              <circle cx="12" cy="5" r="1.6" />
            </svg>
          </span>
          <span className="nav-text">{t('calibration', lang)}</span>
        </button>
      )}
      {/* PM เป็นฟีเจอร์ของแพ็กเกจจ่ายเงิน — server ตัดสินมาใน dbUser.features
          โหมด demo ไม่มี dbUser จึงแสดงตามเดิม (ดู lib/session-flags.ts)

          สามเมนูเดิม (ตาราง · ปฏิทิน · ประวัติ) เป็นงานเดียวกันมองคนละมุม
          ยุบเหลือรายการเดียวแล้วสลับมุมมองด้วยแท็บในหน้า (components/PmTabs)
          ไฮไลต์เมนูต้องดูทั้งสาม PageId ไม่งั้นพอสลับแท็บเมนูจะดับ */}
      {currentUser && isAddonEnabled('maintenance') && (dbUser ? dbUser.features?.pm !== false : true) && !hidden.has('pm') && (
        <button className={`nav-btn${PM_PAGES.includes(page) ? ' active' : ''}`} onClick={() => navTo('pm', currentUser.role !== 'viewer')}>
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.7-.7-.7-2.7 2.4-2.2z" />
            </svg>
          </span>
          <span className="nav-text">{pmMenuLabel(lang)}</span>
        </button>
      )}

      {/* System section — the two "mine" entries (profile, package) come first
          because every signed-in user gets them; then user management (site
          Admin email only), the internal price list, and the user manual.
          License & Renewal was removed: it read tbl_license, which holds a
          single row system-wide, so every other account saw an empty page.
          Don't clear the selected plant for viewers, or their plant-specific
          nav would disappear. */}
      {(showProfile || showUserMgmt || showPackages) && (
        <>
          {/* เดิมชื่อ SYSTEM แต่ข้างในเป็นเรื่องของผู้ใช้เอง ไม่ใช่ของระบบ */}
          <div className="nav-label hide-collapsed">{th ? 'บัญชี' : 'ACCOUNT'}</div>
          {showProfile && (
            <button className={`nav-btn${page === 'profile' ? ' active' : ''}`} onClick={() => setPage('profile')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                </svg>
              </span>
              <span className="nav-text">{t('myProfile', lang)}</span>
            </button>
          )}
          {/* My Package ยุบไปเป็นแท็บใน My Account — ทั้งสองเป็นเรื่อง "ของฉัน"
              เหมือนกันและเปิดดูไม่บ่อย ไม่คุ้มที่จะกินเมนูคนละบรรทัด
              PageId mypackage ยังอยู่ เผื่อลิงก์เดิมและการซ่อนเมนูของ Site Manager */}
          {showUserMgmt && (
            <button className={`nav-btn${page === 'usermgmt' ? ' active' : ''}`} onClick={() => setPage('usermgmt')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <circle cx="9" cy="7" r="3.5" />
                  <path d="M2 21v-1.5A4.5 4.5 0 0 1 6.5 15h5A4.5 4.5 0 0 1 16 19.5V21" />
                  <circle cx="18" cy="8" r="2.5" />
                  <path d="M18 14c2.5 0 4 1.5 4 3.5V19" />
                </svg>
              </span>
              <span className="nav-text">{t('userMgmt', lang)}</span>
            </button>
          )}
          {showPackages && (
            <button className={`nav-btn${page === 'packages' ? ' active' : ''}`} onClick={() => setPage('packages')}>
              <span className="ni">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                  <path d="M21 8v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8" />
                  <rect x="2" y="4" width="20" height="4" rx="1" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
              </span>
              <span className="nav-text">{lang === 'th' ? 'แพ็กเกจ & ราคา' : 'Packages & Pricing'}</span>
            </button>
          )}
        </>
      )}

      {/* Admin / dev tools — momo.test only. In-app User Management removed;
          user & site management lives in the Site Manager admin app. */}
      {canAccessSysPages(currentUser.email) && (
        <>
          <div className="nav-label hide-collapsed">ADMIN</div>
          {/* System Overview + Diagram — จำกัดตาม lib/sys-access */}
          {canAccessSysPages(currentUser.email) && (
            <>
              <button className={`nav-btn${page === 'sysoverview' ? ' active' : ''}`} onClick={() => navTo('sysoverview', true)}>
                <span className="ni">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                    <path d="M12 2 2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </span>
                <span className="nav-text">{lang === 'th' ? 'ภาพรวมระบบ' : 'System Overview'}</span>
              </button>
              <button className={`nav-btn${page === 'sysdiagram' ? ' active' : ''}`} onClick={() => navTo('sysdiagram', true)}>
                <span className="ni">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                    <rect x="3" y="3" width="7" height="5" rx="1" />
                    <rect x="14" y="3" width="7" height="5" rx="1" />
                    <rect x="8.5" y="16" width="7" height="5" rx="1" />
                    <path d="M6.5 8v3.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V8" />
                    <line x1="12" y1="12.5" x2="12" y2="16" />
                  </svg>
                </span>
                <span className="nav-text">{lang === 'th' ? 'แผนภาพระบบ' : 'System Diagram'}</span>
              </button>
              <button className={`nav-btn${page === 'renewals' ? ' active' : ''}`} onClick={() => navTo('renewals', true)}>
                <span className="ni">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                    <polyline points="21 3 21 9 15 9" />
                    <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="15" x2="12" y2="15" />
                  </svg>
                </span>
                <span className="nav-text">{lang === 'th' ? 'ต่ออายุ' : 'Renewals'}</span>
              </button>
              <button className={`nav-btn${page === 'custgroups' ? ' active' : ''}`} onClick={() => navTo('custgroups', true)}>
                <span className="ni">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9.5" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </span>
                <span className="nav-text">{lang === 'th' ? 'กลุ่มลูกค้า' : 'Customer Groups'}</span>
              </button>
              <button className={`nav-btn${page === 'strategy' ? ' active' : ''}`} onClick={() => navTo('strategy', true)}>
                <span className="ni">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4.5" />
                    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
                    <line x1="12" y1="3" x2="12" y2="7.5" />
                    <line x1="12" y1="16.5" x2="12" y2="21" />
                    <line x1="3" y1="12" x2="7.5" y2="12" />
                    <line x1="16.5" y1="12" x2="21" y2="12" />
                  </svg>
                </span>
                <span className="nav-text">{lang === 'th' ? 'กลยุทธ์ & คู่แข่ง' : 'Strategy'}</span>
              </button>
            </>
          )}
          {/* Features & Packages — hidden for now */}
          {false && (
          <button className={`nav-btn${page === 'features' ? ' active' : ''}`} onClick={() => navTo('features', true)}>
            <span className="ni">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6" />
                <rect x="2" y="7" width="20" height="5" rx="1" />
                <line x1="12" y1="7" x2="12" y2="21" />
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C12 2 12 7 12 7z" />
              </svg>
            </span>
            <span className="nav-text">{lang === 'th' ? 'ฟีเจอร์และแพ็กเกจ' : 'Features & Packages'}</span>
          </button>
          )}
        </>
      )}

      {/* Roadmap — hidden for now */}
      {false && (
        <>
          <div className="nav-label hide-collapsed">PROJECT</div>
          <button className={`nav-btn${page === 'roadmap' ? ' active' : ''}`} onClick={() => setPage('roadmap')}>
            <span className="ni">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
            </span>
            <span className="nav-text">Roadmap</span>
          </button>
        </>
      )}

      {/* Theme + language — mobile only (on desktop these live in the topbar).
          Shown/hidden via .sidebar-mobile-controls CSS. */}
      <div className="sidebar-mobile-controls">
        <button className="sb-toggle" onClick={toggleTheme}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
          {lang === 'th' ? 'ธีม' : 'Theme'}
        </button>
        <button className="sb-toggle" onClick={toggleLang}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
            <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9z" />
          </svg>
          {lang === 'th' ? 'EN' : 'TH'}
        </button>
      </div>
      {/* คู่มือ — เป็นเอกสารช่วยเหลือ ไม่ใช่งานประจำวันและไม่ใช่เรื่องบัญชี
          จึงอยู่ล่างสุดแยกด้วยเส้นคั่น ไม่ต้องมีหัวข้อหมวดของตัวเอง */}
      {showManual && (
        <button
          className={`nav-btn${page === 'manual' ? ' active' : ''}`}
          onClick={() => navTo('manual', currentUser.role !== 'viewer')}
          style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--glass-border)', borderRadius: 0 }}
        >
          <span className="ni">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="ni">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              <line x1="8" y1="7" x2="15" y2="7" /><line x1="8" y1="11" x2="13" y2="11" />
            </svg>
          </span>
          <span className="nav-text">{t('manual', lang)}</span>
        </button>
      )}


      {/* Footer */}
      {/* footer removed */}
    </div>
    </>
  );
}
